import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { cn } from "./cn";

/**
 * WO-P5 §SHOWCASE-INTRO — presentation-only tilt for the Grid slab.
 *
 * Behaviour (desktop):
 *   1. On mount the wrapper starts in `tilted` mode: a CSS-only
 *      pseudo-isometric transform is applied and the wrapped canvas
 *      is made non-interactive (`pointer-events: none`) so that
 *      normal wheel keeps scrolling the page and no accidental
 *      hover / click can hit-test on the tilted view (which would
 *      diverge from canonical world coordinates).
 *   2. When the pointer stays over the Grid area for
 *      `HOVER_ACTIVATION_MS` (~1s) the wrapper switches to
 *      `transitioning`; the CSS transform interpolates over
 *      600 ms to the identity matrix.
 *   3. The REAL `transitionend` event on the `transform` property
 *      triggers the switch to `top-down`. Only in `top-down` are
 *      pointer events forwarded to the canvas — this is the HARD
 *      interaction gate required by the WO.
 *   4. Once `top-down` is reached the intro is over for this
 *      section visit: the wrapper never re-tilts on subsequent
 *      mouseout / panel hover / accidental movement.
 *
 * WO-P5 iter4 §HOVER-ONLY — Claudio review: no auto-activation
 * timer. The tilt resolves ONLY when the user genuinely hovers
 * the Grid (or, for keyboard a11y, focuses into it). Waiting on
 * the tilted grid indefinitely is intentional: the tilt is a
 * showcase, not a page-load animation.
 *
 * Behaviour (mobile & reduced-motion): the intro is skipped
 * entirely — the wrapper mounts directly in `top-down` mode and
 * interaction is ON immediately. The WO explicitly favours mobile
 * usability over cinematic parity.
 *
 * IMPORTANT: this is a presentation-only wrapper. It does NOT
 * change screenToWorld / worldToScreen / camera math / hit-test
 * / canonical geometry / zone manifest. When interaction is
 * active the transform is exactly identity, so any pointer event
 * on the canvas maps 1:1 to the same pixel it would have mapped
 * to without this wrapper.
 */

export type ShowcaseMode = "tilted" | "transitioning" | "top-down";

const HOVER_ACTIVATION_MS = 1000;
const TRANSITION_MS = 600;

export interface ShowcaseIntroProps {
  children: ReactNode;
  /**
   * When false (mobile / reduced-motion / already-visited), the
   * wrapper mounts directly in `top-down` mode.
   */
  enableTilt: boolean;
  /**
   * Optional callback fired whenever the mode changes. Consumers
   * can use it to update analytics / dev overlays. Never used for
   * gating: the wrapper itself owns the interaction gate.
   */
  onModeChange?: (mode: ShowcaseMode) => void;
}

/**
 * Returns true when the current environment prefers reduced
 * motion. Guarded for SSR / test envs without `matchMedia`.
 */
function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/**
 * Test-environment guard.
 *
 * Playwright / WebDriver sets `navigator.webdriver === true`. In
 * that environment we default to skipping the tilt so the pre-
 * existing e2e suite (which was authored before this WO and does
 * not know about the intro state machine) keeps clicking the
 * canvas the moment it mounts. The WO-P5 e2e spec explicitly
 * opts INTO the tilt by appending `?showcase=on` to the URL.
 *
 * Real users are unaffected (`navigator.webdriver === false` in
 * production browsers, hence the intro plays).
 */
function shouldForceTopDownForTests(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const nav = window.navigator as Navigator & { webdriver?: boolean };
    if (!nav.webdriver) return false;
    const url = new URL(window.location.href);
    // Opt-in flag: `?showcase=on` keeps the intro alive under a
    // WebDriver runner (used by the WO-P5 spec).
    if (url.searchParams.get("showcase") === "on") return false;
    return true;
  } catch {
    return false;
  }
}

export function ShowcaseIntro({
  children,
  enableTilt,
  onModeChange,
}: ShowcaseIntroProps) {
  const initialMode: ShowcaseMode = useMemo(() => {
    if (!enableTilt) return "top-down";
    if (prefersReducedMotion()) return "top-down";
    if (shouldForceTopDownForTests()) return "top-down";
    return "tilted";
  }, [enableTilt]);

  const [mode, setMode] = useState<ShowcaseMode>(initialMode);
  const modeRef = useRef<ShowcaseMode>(initialMode);
  const hoverTimerRef = useRef<number | null>(null);
  const activatedRef = useRef(initialMode === "top-down");
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const updateMode = useCallback(
    (next: ShowcaseMode) => {
      modeRef.current = next;
      setMode(next);
      onModeChange?.(next);
    },
    [onModeChange],
  );

  // Once the wrapper reaches `top-down` for this visit, the intro
  // is final: mark activated so hover handlers become no-ops.
  useEffect(() => {
    if (mode === "top-down") activatedRef.current = true;
  }, [mode]);

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current != null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  const armActivation = useCallback(() => {
    if (activatedRef.current) return;
    if (modeRef.current !== "tilted") return;
    // Idempotent: don't re-arm on every mousemove — the first move
    // over the tilted grid arms the 1s activation timer and any
    // subsequent movement just keeps the hover alive.
    if (hoverTimerRef.current != null) return;
    hoverTimerRef.current = window.setTimeout(() => {
      hoverTimerRef.current = null;
      if (activatedRef.current) return;
      if (modeRef.current !== "tilted") return;
      updateMode("transitioning");
    }, HOVER_ACTIVATION_MS);
  }, [updateMode]);

  // Native document-level mousemove capture — React synthetic
  // handlers sometimes miss the very first `mousemove` under
  // headless webdriver (the target element is under a transformed
  // wrapper and pointer events aren't re-dispatched). The native
  // listener is authoritative: it checks whether the pointer is
  // actually inside the wrapper's current bounding rect.
  useEffect(() => {
    if (activatedRef.current) return;
    if (modeRef.current !== "tilted") return;
    const el = wrapperRef.current;
    if (!el) return;
    const handler = (e: MouseEvent) => {
      if (activatedRef.current) return;
      if (modeRef.current !== "tilted") return;
      const rect = el.getBoundingClientRect();
      const inside =
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;
      if (!inside) {
        if (hoverTimerRef.current != null) clearHoverTimer();
        return;
      }
      armActivation();
    };
    document.addEventListener("mousemove", handler, { passive: true });
    return () => document.removeEventListener("mousemove", handler);
  }, [mode, armActivation, clearHoverTimer]);

  // A11y — keyboard users never move a pointer over the Grid.
  // When focus enters the wrapper (Tab through the page reaches
  // the Grid section) we skip the 1s hover delay and go straight
  // to `transitioning`. Pointer-users are unaffected.
  const onFocusCapture = useCallback(() => {
    if (activatedRef.current) return;
    if (modeRef.current !== "tilted") return;
    updateMode("transitioning");
  }, [updateMode]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (activatedRef.current) return;
      if (modeRef.current !== "tilted") return;
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      updateMode("transitioning");
    },
    [updateMode],
  );

  useEffect(() => clearHoverTimer, [clearHoverTimer]);

  // Safety net: if the wrapper is in `transitioning` for more than
  // TRANSITION_MS * 2, force `top-down`. This guards against DOM
  // engines that swallow transitionend for the `transform` property
  // when the element is detached / re-mounted mid-flight.
  useEffect(() => {
    if (mode !== "transitioning") return;
    const timer = window.setTimeout(() => {
      if (modeRef.current === "transitioning") updateMode("top-down");
    }, TRANSITION_MS * 2);
    return () => window.clearTimeout(timer);
  }, [mode, updateMode]);

  const onTransitionEnd = useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      if (e.propertyName !== "transform") return;
      if (modeRef.current !== "transitioning") return;
      updateMode("top-down");
    },
    [updateMode],
  );

  // Interaction gate: only forward pointer events in `top-down`.
  // The wrapper itself must always receive pointer events (otherwise
  // it could not detect hover to trigger the intro) so we gate a
  // full-bleed inner shield instead: while tilted/transitioning a
  // transparent overlay swallows clicks and mousedowns above the
  // canvas without affecting our own hover listeners. Normal wheel
  // events on the shield bubble to the page (default behaviour) so
  // page scroll is preserved during the intro.
  const interactive = mode === "top-down";

  // WO-VRD-2 §SHOWCASE-DEPTH — stronger presentation-only perspective so
  // the slab first reads as a physical holographic object seen with
  // meaningful depth (Stage 5). Lifecycle + interaction gate unchanged:
  // in `top-down` the transform is EXACT identity ("none"), so canonical
  // canvas coordinates map 1:1 the instant interaction is enabled.
  const transform =
    mode === "top-down"
      ? "none"
      : "perspective(1200px) rotateX(36deg) scale(0.9) translateY(-3%)";

  const transition =
    mode === "transitioning"
      ? `transform ${TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`
      : "none";

  // WO-P5 iter4 §FLUIDITY — promote the wrapper to a GPU layer
  // ONLY during the 600ms transition; leave it as a plain element
  // otherwise so the browser can reclaim the compositor layer and
  // avoid keeping a large offscreen texture permanently in memory.
  const willChange = mode === "transitioning" ? "transform" : "auto";

  return (
    <div
      ref={wrapperRef}
      data-testid="showcase-wrapper"
      data-mode={mode}
      onTransitionEnd={onTransitionEnd}
      onFocusCapture={mode === "tilted" ? onFocusCapture : undefined}
      onKeyDown={mode === "tilted" ? onKeyDown : undefined}
      tabIndex={mode === "tilted" ? 0 : -1}
      className={cn("absolute inset-0")}
      style={{
        transform,
        transformOrigin: "50% 60%",
        transition,
        willChange,
        outline: "none",
      }}
    >
      {children}
      {!interactive && (
        <div
          data-testid="showcase-interaction-shield"
          aria-hidden
          className="absolute inset-0 z-canvas"
          style={{
            // Transparent shield above the canvas: catches click /
            // mousedown / contextmenu so the canvas hit-test cannot
            // fire while the tilt is engaged. Wheel/scroll events
            // pass through by default because we do not attach any
            // wheel listener here.
            background: "transparent",
            pointerEvents: "auto",
            cursor: "default",
          }}
        />
      )}
    </div>
  );
}
